import React, {useEffect, useState} from "react"
import {connect} from "react-redux"
import "pages/Styles/ProfileV2.sass"
import {httpClient} from "../../funcs";
import {openInNewTab} from "../../methods/openInNewTab";
import notifyUser, {notifyAboutError} from "../../methods/notifyUser";
import {CopyToClipboard} from "react-copy-to-clipboard";
import {ReactComponent as Delete} from "../Icons/size24/Delete.svg";
import {checkPushPermission, registerWebPush, unregisterWebPush, isPushAvailable, getCurrentPushSubscription} from "../../utils/webPushNotifications";
import { loadRoles } from "../../utils/workWithRoles";
import { loadSelf } from "../../methods/loadSelf";
import MaxIntegrationSetupPopup from "./IntegrationsPopup/MaxIntegrationSetupPopup";

type Props = {
    $store: any,
    $commitToStore: Function
}

enum TGBotStatus {
    awaitingConfirmation = "AWAITING_CONFIRMATION",
    confirmed = "CONFIRMED"
}

enum MAXBotStatus {
    awaitingConfirmation = "AWAITING_CONFIRMATION",
    confirmed = "CONFIRMED"
}

let TGBotIntegrationView: any = function TGBotIntegrationView(props: Props) {
    const [pushEnabled, setPushEnabled] = useState(false);
    const [pushAvailable, setPushAvailable] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [grantedByYouRoles, setGrantedByYouRoles] = useState<any[]>([]);
    const [maxWizardPopupOpen, setMaxWizardPopupOpen] = useState(false);

    useEffect(() => {
        checkPushAvailability();
        loadUserRoles();
    }, []);

    async function loadUserRoles() {
        const { grantedByYouRoles } = await loadRoles(props.$store.userSession?._id, props);
        setGrantedByYouRoles(grantedByYouRoles);
    }
    
    async function checkPushAvailability() {
        // Проверяем техническую возможность
        const available = await isPushAvailable();
        setPushAvailable(available);
        
        if (!available) {
            setPushEnabled(false);
            return;
        }

        // Проверяем разрешения и наличие активной подписки
        const permission = await checkPushPermission();
        const hasActiveSubscription = await getCurrentPushSubscription();
        
        setPushEnabled(permission === 'granted' && hasActiveSubscription);
    }

    async function togglePushNotifications() {
        try {
            setIsLoading(true);
            if (!pushEnabled) {
                await registerWebPush();
                setPushEnabled(true);
            } else {
                await unregisterWebPush();
                setPushEnabled(false);
            }
        } catch (error) {
            notifyAboutError(props, error);
            // В случае ошибки перепроверяем состояние
            await checkPushAvailability();
        } finally {
            setIsLoading(false);
        }
    }

    function makeWebPushSection() {
        if (!pushAvailable) {
            return (
                <div>
                    <div className="Body_16_v2_secondary">
                        Уведомления в браузере недоступны в вашем браузере
                    </div>
                </div>
            );
        }

        return (
            <div>
                <div className="titleContainer">
                    <div className="Subheader_18">
                        Уведомления в браузере
                    </div>
                    <label className={`switch ${isLoading ? 'disabled' : ''}`}>
                        <input
                            type="checkbox"
                            checked={pushEnabled}
                            onChange={togglePushNotifications}
                            disabled={isLoading}
                        />
                        <span className="slider round"></span>
                    </label>
                </div>
                <div className="Body_16_v2_secondary" style={{marginTop: "10px"}}>
                    {isLoading 
                        ? "Подождите..." 
                        : (pushEnabled 
                            ? `Вы будете получать уведомления о новых подписанных документах в браузере`
                            : `Включите, чтобы получать уведомления о новых подписанных документах в браузере`
                        )
                    }
                </div>
            </div>
        );
    }

    async function requestCodeForTGBot() {
        try {
            await httpClient.post('/integrate-tg-bot');
            await loadSelf(props);
        } catch (error) {
            notifyAboutError(props, error);
        }
    }

    async function requestCodeForMaxBot() {
        try {
            await httpClient.post('/integrate-max-bot');
            await loadSelf(props);
        } catch (error) {
            notifyAboutError(props, error);
        }
    }

    async function turnOffTGBot(contact: any) {
        try {
            await httpClient.post('/turn-off-tg-bot', { contact });
            await loadSelf(props);
        } catch (error) {
            notifyAboutError(props, error);
        }
    }

    async function turnOffMaxBot(contact: any) {
        try {
            await httpClient.post('/turn-off-max-bot', { contact });
            await loadSelf(props);
        } catch (error) {
            notifyAboutError(props, error);
        }
    }

    function tgContacts() {
        return props.$store.userSession?.tg_contacts ?? []
    }

    function maxContacts() {
        return props.$store.userSession?.max_contacts ?? []
    }

    function hasConfirmedMaxContact() {
        const currentEmployeeId = props.$store.userSession?.employee_id;
        // Сотрудник видит только свой запрос, владелец — свой
        if (currentEmployeeId) {
            return maxContacts().some((contact: any) =>
                contact &&
                contact.status === MAXBotStatus.confirmed &&
                contact.created_by_employee_id === currentEmployeeId
            );
        }
        return maxContacts().some((contact: any) =>
            contact &&
            contact.status === MAXBotStatus.confirmed &&
            !contact.created_by_employee_id
        );
    }

    function hasActiveContact() {
        const currentUserId = props.$store.userSession?._id;
        const currentEmployeeId = props.$store.userSession?.employee_id;
        
        // Если пользователь является сотрудником в бизнес-аккаунте
        if (currentEmployeeId) {
            // Ищем только свой запрос
            return tgContacts().some((contact: any) => 
                contact && 
                contact.status === TGBotStatus.awaitingConfirmation && 
                contact.created_by_employee_id === currentEmployeeId
            );
        }
        
        // Для владельца бизнеса
        return tgContacts().some((contact: any) => {
            if (!contact) {
                return false;
            }
            // Если есть подтвержденный контакт владельца
            if (contact.status === TGBotStatus.confirmed && !contact.created_by_employee_id) {
                return true;
            }
            // Если есть запрос на владельца
            if (contact.status === TGBotStatus.awaitingConfirmation && !contact.created_by_employee_id) {
                return true;
            }
            return false;
        });
    }

    function hasActiveMaxContact() {
        const currentEmployeeId = props.$store.userSession?.employee_id;

        if (currentEmployeeId) {
            return maxContacts().some((contact: any) =>
                contact &&
                contact.status === MAXBotStatus.awaitingConfirmation &&
                contact.created_by_employee_id === currentEmployeeId
            );
        }

        return maxContacts().some((contact: any) => {
            if (!contact) {
                return false;
            }
            if (contact.status === MAXBotStatus.confirmed && !contact.created_by_employee_id) {
                return true;
            }
            if (contact.status === MAXBotStatus.awaitingConfirmation && !contact.created_by_employee_id) {
                return true;
            }
            return false;
        });
    }

    function makeContactView(contact: any) {
        if (!contact) {
            return null;
        }

        const currentEmployeeId = props.$store.userSession?.employee_id;
        
        // Если пользователь сотрудник, показываем только свой запрос
        if (currentEmployeeId && contact.created_by_employee_id !== currentEmployeeId) {
            return null;
        }

        if (contact.status == TGBotStatus.awaitingConfirmation) {
            return <div>
                <div className="row-data" style={{marginTop: "10px"}}>
                    <div>
                        <div>
                            <div className={"buttonsContainer"}>
                                <div className="Body_16_v2_secondary">
                                    Отправьте боту <a href={`https://t.me/${process.env.REACT_APP_TG_ENTREPRENEUR_NOTIFICATIONS_BOT_NAME}`} target="_blank">@{process.env.REACT_APP_TG_ENTREPRENEUR_NOTIFICATIONS_BOT_NAME}</a> свой контакт:
                                </div>
                            
                                <button
                                    className="new smallIconed"
                                    onClick={() => {
                                        turnOffTGBot(contact)
                                    }}
                                >
                                    <Delete className="icon dark"/>
                                </button>
                            </div>
                        </div>
                        <div className="Body_16_v2_secondary" style={{marginTop: "10px", color: "#666"}}>
                            Для подключения используйте аккаунт телеграм с номером телефона: {contact.employee_phone_number || props.$store.userSession?.phone_number}
                        </div>
                        <div className="buttonsContainer" style={{marginTop: "10px"}}>
                            <button
                                className="new action-button secondary"
                                onClick={() => {
                                    notifyUser(props, "Телеграм откроется в течении 4 секунд");
                                    setTimeout(() => {
                                        openInNewTab(`https://t.me/${process.env.REACT_APP_TG_ENTREPRENEUR_NOTIFICATIONS_BOT_NAME}`)
                                    }, 4000)
                                }}
                            >
                                <span className="button-text">Открыть бота</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        }

        if (contact.status == TGBotStatus.confirmed) {
            return <div>
                <div className={"buttonsContainer"}>
                    {
                        contact.first_name &&
                        <div className="Body_16_v2">
                            {contact.first_name}
                        </div>
                    }
                    {
                        contact.username &&
                        <a href={`https://t.me/${contact.username}`} target="_blank">@{contact.username}</a>
                    }
                    {
                        !contact.first_name && !contact.username &&
                        <div className="Body_16_v2">
                            Телеграм бот подключен
                        </div>
                    }


                    <button
                        className="new smallIconed"
                        onClick={async () => {
                            await turnOffTGBot(contact)
                        }}
                    >
                        <Delete className="icon dark"/>
                    </button>
                </div>
            </div>
        }
    }

    function makeMaxContactView(contact: any) {
        if (!contact) {
            return null;
        }

        const currentEmployeeId = props.$store.userSession?.employee_id;

        if (currentEmployeeId && contact.created_by_employee_id !== currentEmployeeId) {
            return null;
        }

        const maxBotId = process.env.REACT_APP_MAX_ENTREPRENEUR_NOTIFICATIONS_BOT_NAME || "id6330100935_bot";
        const maxBotLink = `https://max.ru/${maxBotId}`;

        if (contact.status == MAXBotStatus.awaitingConfirmation) {
            return <div>
                <div className="row-data" style={{marginTop: "10px"}}>
                    <div>
                        <div>
                            <div className={"buttonsContainer"}>
                                <div className="Body_16_v2_secondary">
                                    Отправьте MAX боту свой контакт (номер телефона) через кнопку в чате с ботом:
                                </div>

                                <button
                                    className="new smallIconed"
                                    onClick={() => {
                                        turnOffMaxBot(contact)
                                    }}
                                >
                                    <Delete className="icon dark"/>
                                </button>
                            </div>
                        </div>
                        <div className="Body_16_v2_secondary" style={{marginTop: "10px", color: "#666"}}>
                            Для подключения используйте аккаунт MAX с номером телефона: {contact.employee_phone_number || props.$store.userSession?.phone_number}
                        </div>
                        <div className="buttonsContainer" style={{marginTop: "10px"}}>
                            <button
                                className="new action-button secondary"
                                onClick={() => {
                                    notifyUser(props, "MAX откроется в течении 4 секунд");
                                    setTimeout(() => {
                                        openInNewTab(maxBotLink)
                                    }, 4000)
                                }}
                            >
                                <span className="button-text">Открыть бота</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        }

        if (contact.status == MAXBotStatus.confirmed) {
            return <div>
                <div className={"buttonsContainer"}>
                    {
                        contact.name &&
                        <div className="Body_16_v2">
                            {contact.name}
                        </div>
                    }

                    {
                        !contact.name &&
                        <div className="Body_16_v2">
                            MAX бот подключен
                        </div>
                    }

                    <button
                        className="new smallIconed"
                        onClick={async () => {
                            await turnOffMaxBot(contact)
                        }}
                    >
                        <Delete className="icon dark"/>
                    </button>
                </div>
            </div>
        }
    }

    function makeTelegramSection() {
        return (
            <div className="profile-section-card ui-kit-v2">
                <div className="profile-section-title" style={{marginBottom: "10px"}}>
                    Уведомления в телеграм
                </div>

                <div className="Body_16_v2_secondary" style={{textAlign: "left"}}>
                    Настройте телеграм бота, чтобы получать уведомления о новых подписанных документах прямо в мессенджере
                </div>

                <div className="tg-bot-video-links">
                    <div className="video-instructions-title">Видео-инструкции по подключению:</div>
                    <div className="video-link-item">
                        <span>• </span><a href="https://rutube.ru/video/c25adedaf470399665a50ce082488b9c/" target="_blank" rel="noopener noreferrer">Как подключить бота владельцу</a>
                    </div>
                    <div className="video-link-item">
                        <span>• </span><a href="https://rutube.ru/video/4f6d774a0f03c4ae50b2f833c480cf43/" target="_blank" rel="noopener noreferrer">Как подключить бота сотруднику</a>
                    </div>
                    <div className="video-link-item">
                        <span>• </span><a href="https://rutube.ru/video/f0024f7658f7b61caf32d0ad1df227e1/" target="_blank" rel="noopener noreferrer">Как подключить бота для группы</a>
                    </div>
                </div>

                {grantedByYouRoles.length > 0 && (
                    <div className="Body_16_v2_secondary" style={{
                        marginTop: "10px",
                        color: "#666",
                        backgroundColor: "#FFF9E6",
                        padding: "12px 16px",
                        borderRadius: "8px",
                        border: "1px solid #FFE7A3",
                        marginBottom: "10px"
                    }}>
                        ⚠️ Если вы подключаете бота для сотрудника, то именно сотрудник должен подключить бота в своем профиле ОкиДоки. Номер телефона сотрудника в Telegram должен совпадать с номером телефона для входа в ОкиДоки.
                    </div>
                )}

                {
                    tgContacts().map((contact: any) => {
                        return makeContactView(contact)
                    })
                }

                {!hasActiveContact() && (
                    <>
                        <hr className="profile-section-divider"/>
                        <div className="profile-section-row">
                            <button
                                className="new action-button primary"
                            onClick={async () => {
                                await requestCodeForTGBot()
                            }}
                        >
                            <span className="button-text">Подключить телеграм бот</span>
                            </button>
                        </div>
                    </>
                )}
            </div>
        );
    }

    function makeMaxSection() {
        return (
            <div className="profile-section-card ui-kit-v2">
                <div className="profile-section-title" style={{marginBottom: "10px"}}>
                    Уведомления в MAX
                </div>

                <div className="Body_16_v2_secondary" style={{textAlign: "left"}}>
                    Настройте MAX бота, чтобы получать уведомления о новых подписанных документах прямо в мессенджере
                </div>

                <div style={{marginTop: "10px", marginBottom: "10px"}}>
                    <button
                        className="new action-button secondary"
                        onClick={() => setMaxWizardPopupOpen(true)}
                    >
                        <span className="button-text">Пошаговая инструкция</span>
                    </button>
                </div>

                {grantedByYouRoles.length > 0 && (
                    <div className="Body_16_v2_secondary" style={{
                        marginTop: "10px",
                        color: "#666",
                        backgroundColor: "#FFF9E6",
                        padding: "12px 16px",
                        borderRadius: "8px",
                        border: "1px solid #FFE7A3",
                        marginBottom: "10px"
                    }}>
                        ⚠️ Если вы подключаете бота для сотрудника, то именно сотрудник должен подключить бота в своем профиле ОкиДоки. Номер телефона сотрудника в MAX должен совпадать с номером телефона для входа в ОкиДоки.
                    </div>
                )}

                {
                    maxContacts().map((contact: any) => {
                        return makeMaxContactView(contact)
                    })
                }

                {!hasActiveMaxContact() && (
                    <>
                        <hr className="profile-section-divider"/>
                        <div className="profile-section-row">
                            <button
                                className="new action-button primary"
                                onClick={async () => {
                                    await requestCodeForMaxBot()
                                }}
                            >
                                <span className="button-text">Подключить MAX бот</span>
                            </button>
                        </div>
                    </>
                )}
            </div>
        );
    }

    return <div>
        <div className="profile-section-card ui-kit-v2">
            {makeWebPushSection()}
        </div>
        
        <div style={{marginTop: "20px"}}>
            {makeTelegramSection()}
        </div>

        <div style={{marginTop: "20px"}}>
            {makeMaxSection()}
        </div>

        {maxWizardPopupOpen && (
            <MaxIntegrationSetupPopup
                close={() => setMaxWizardPopupOpen(false)}
                $store={props.$store}
                $commitToStore={props.$commitToStore}
            />
        )}
    </div>
}

TGBotIntegrationView = connect(
    (store) => ({ $store: store }),
    (dispatch) => ({
        $commitToStore: (data: any) => dispatch({ ...data, type: 'S' })
    })
)(TGBotIntegrationView)

export default TGBotIntegrationView